/**
 * Risk Dashboard page: portfolio-level risk assessment — concentration,
 * sector exposure, and liquidity risk — for a selected portfolio.
 */

import React, { useEffect, useState } from "react";
import { apiClient } from "@/api/client";
import type { Portfolio, PortfolioRiskAssessment } from "@/types";
import ScoreBadge from "@/components/research/ScoreBadge";

const RATING_COPY: Record<PortfolioRiskAssessment["overall_rating"], string> = {
  low: "Low risk",
  moderate: "Moderate risk",
  elevated: "Elevated risk",
  high: "High risk",
};

export default function RiskDashboard(): React.ReactElement {
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [risk, setRisk] = useState<PortfolioRiskAssessment | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiClient
      .get<Portfolio[]>("/portfolios")
      .then(({ data }) => {
        setPortfolios(data);
        if (data.length > 0) setSelectedId(data[0].id);
      })
      .catch(() => setError("Unable to load portfolios."))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    setRisk(null);
    apiClient
      .get<PortfolioRiskAssessment>(`/portfolios/${selectedId}/risk`)
      .then(({ data }) => setRisk(data))
      .catch(() => setError("Unable to load risk assessment for this portfolio."));
  }, [selectedId]);

  if (isLoading) {
    return <p className="text-sm text-mist-400">Loading portfolios…</p>;
  }

  if (portfolios.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold">Risk dashboard</h1>
          <p className="mt-1 text-sm text-mist-400">Portfolio-level concentration, sector, and liquidity risk.</p>
        </div>
        <div className="panel flex flex-col items-center gap-2 py-16 text-center">
          <p className="text-sm font-medium text-mist-200">No portfolios yet</p>
          <p className="text-sm text-mist-400">Create a portfolio and add positions to see its risk profile.</p>
        </div>
      </div>
    );
  }

  const sectorEntries = risk ? Object.entries(risk.sector_exposure_pct) : [];
  const maxSectorPct = Math.max(1, ...sectorEntries.map(([, pct]) => pct));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Risk dashboard</h1>
          <p className="mt-1 text-sm text-mist-400">Portfolio-level concentration, sector, and liquidity risk.</p>
        </div>
        <select
          value={selectedId ?? ""}
          onChange={(e) => setSelectedId(e.target.value)}
          className="field-input w-56"
        >
          {portfolios.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </div>

      {error && <p className="text-sm text-signal-rose">{error}</p>}

      {risk && (
        <>
          <div className="panel p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-mist-400">Overall rating</p>
                <p className="mt-1 text-xl font-semibold text-mist-100">
                  {RATING_COPY[risk.overall_rating]}
                </p>
              </div>
              <ScoreBadge score={risk.confidence} label="confidence" />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="panel p-5">
              <p className="text-xs uppercase tracking-wide text-mist-400">Concentration (HHI)</p>
              <p className="mt-1 font-mono text-2xl text-mist-100">
                {risk.concentration_hhi?.toFixed(0) ?? "—"}
              </p>
              <p className="mt-1 text-xs text-mist-400">Lower is more diversified (0-10,000 scale)</p>
            </div>
            <div className="panel p-5">
              <p className="text-xs uppercase tracking-wide text-mist-400">Liquidity risk score</p>
              <p className="mt-1 font-mono text-2xl text-mist-100">
                {risk.liquidity_risk_score?.toFixed(0) ?? "—"}
                <span className="text-sm text-mist-400">/100</span>
              </p>
              <p className="mt-1 text-xs text-mist-400">Higher means easier to exit positions</p>
            </div>
          </div>

          <div className="panel p-6">
            <h2 className="text-sm font-semibold text-mist-100">Sector exposure</h2>
            {sectorEntries.length === 0 ? (
              <p className="mt-3 text-sm text-mist-400">No positions to break down yet.</p>
            ) : (
              <div className="mt-4 space-y-3">
                {sectorEntries.map(([sector, pct]) => (
                  <div key={sector}>
                    <div className="mb-1 flex justify-between text-xs text-mist-400">
                      <span>{sector}</span>
                      <span>{pct.toFixed(1)}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-ink-800">
                      <div
                        className="h-2 rounded-full bg-signal-teal"
                        style={{ width: `${(pct / maxSectorPct) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {risk.notes.length > 0 && (
            <div className="panel p-5">
              <h2 className="text-sm font-semibold text-mist-100">Notes</h2>
              <ul className="mt-2 space-y-1 text-sm text-mist-300">
                {risk.notes.map((note, i) => (
                  <li key={i}>• {note}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
